import {
  AddAddressRequest,
  ApplyManualDocumentRequest,
  ApplyRecognisedDocumentRequest,
  ApplyTravelDocumentRequest,
  DefaultApi,
  PostAnswersRequest,
  RecogniseDocumentRequest,
  SetConsentRequest,
  StartWithBookingIdAndSurnamePostRequest,
} from "api/apis";
import {
  Configuration,
  ErrorContext,
  HTTPRequestInit,
  RequestOpts,
} from "api/runtime";
import { getAzureApiGatewayHeaders } from "config/config";
import Logging from "logging";
import { InternationalPassportToApiTravelDocument } from "models/documents/international-passport";
import PaxSession, {
  PaxSessionFromApi,
  PaxSessionId,
} from "models/session/pax-session";
import MultiPaxSession, {
  MultiPaxSessionFromApi,
  MultiPaxSessionId,
} from "models/session/session";
import { v4 as uuidv4 } from "uuid";

import { ApplyDocumentErrorWrapper } from "./ApplyDocumentResponse";
import SubmitManualDocumentResponse, {
  ApplyManualDocumentResponseFromApi,
} from "./SubmitManualDocumentResponse";
import SubmitPassportRequest from "./SubmitPassportRequest";
import SubmitPassportResponse, {
  ApplyPassportResponseFromApi,
} from "./SubmitPassportResponse";
import DataProviderGeneralException from "./data-provider-errors";
import {
  PaxCheckAnswer,
  PaxCheckDocumentProvisionMethod,
  PaxCheckManualDocument,
  PaxCheckRecogniseDocumentResult,
  PaxCheckStartWithBookingIdAndSurnameResponseData,
} from "api/models";

declare global {
  interface Window {
    PAXCHECK_BASE_PATH: string | null;
  }
}

const addRandomRequestIdToHeader = (requestContext: {
  init: HTTPRequestInit;
  context: RequestOpts;
}): Promise<RequestInit> =>
  Promise.resolve({
    ...requestContext.init,
    headers: {
      ...requestContext.init.headers,
      "X-Request-Id": uuidv4(),
    },
  });

class DataProvider {
  static shared: DataProvider;

  api: DefaultApi;
  locale: string;
  chosenPaxSessionIds: string[] | null;

  static init(
    token: string | undefined,
    locale: string,
    chosenPaxSessionIds: string[] | null
  ) {
    DataProvider.shared = new DataProvider(token, locale, chosenPaxSessionIds);
  }

  private constructor(
    token: string | undefined,
    locale: string,
    chosenPaxSessionIds: string[] | null
  ) {
    const azureApiGatewayHeaders = getAzureApiGatewayHeaders();

    const configuration = new Configuration({
      basePath:
        window.PAXCHECK_BASE_PATH || process.env.REACT_APP_API_BASE_PATH,
      ...(token !== undefined
        ? { accessToken: token }
        : { accessToken: "access_token_is_missing" }),
      ...(azureApiGatewayHeaders ? { headers: azureApiGatewayHeaders } : {}),
      middleware: [
        {
          post: async (context) => {
            if (context.response?.status !== 200) {
              const headers = new Headers(context.init.headers);
              const requestId = headers?.get("X-Request-Id");
              const body = await context.response.text();

              Logging.log(
                "data-provider",
                context.url,
                `Error response. request-id: ${requestId}. err: ${body}`
              );
            }
            return Promise.resolve(context.response);
          },
          onError: (context: ErrorContext) => {
            const requestId = context.response?.headers.get("X-Request-Id");
            Logging.log(
              "data-provider",
              context.url,
              `Request error. request-id: ${requestId}. err: ${context.error}`
            );
            return Promise.reject(context.error);
          },
        },
      ],
    });

    this.api = new DefaultApi(configuration);
    this.locale = locale;
    this.chosenPaxSessionIds = chosenPaxSessionIds;
  }

  async auth(
    bookingId: string,
    surname: string,
    recaptchaResponse: string
  ): Promise<PaxCheckStartWithBookingIdAndSurnameResponseData> {
    const req: StartWithBookingIdAndSurnamePostRequest = {
      paxCheckStartWithBookingIdAndSurnameRequest: {
        bookingId: bookingId,
        surname: surname,
        recaptchaResponse: recaptchaResponse,
      },
    };

    const res = await this.api.startWithBookingIdAndSurnamePost(
      req,
      addRandomRequestIdToHeader
    );

    if (res.data) {
      // success
      return res.data;
    }

    // general api error
    throw DataProviderGeneralException.fromApi(res.error);
  }

  async getSession(sessionId: string): Promise<MultiPaxSession> {
    const res = await this.api.getSession(
      {
        sessionId: sessionId,
        acceptLanguage: this.locale,
      },
      addRandomRequestIdToHeader
    );

    if (res.data) {
      // success
      return MultiPaxSessionFromApi(res.data, this.chosenPaxSessionIds);
    }

    // general api error
    throw DataProviderGeneralException.fromApi(res.error);
  }

  async submitConsent(
    sessionId: MultiPaxSessionId,
    isConsentGiven: boolean
  ): Promise<MultiPaxSession> {
    const req: SetConsentRequest = {
      sessionId,
      paxCheckSetConsentRequest: {
        consent: isConsentGiven,
      },
      acceptLanguage: this.locale,
    };

    const res = await this.api.setConsent(req, addRandomRequestIdToHeader);

    if (res.data) {
      // success
      return MultiPaxSessionFromApi(res.data, this.chosenPaxSessionIds);
    }

    // general api error
    throw DataProviderGeneralException.fromApi(res.error);
  }

  async submitAddress(
    sessionId: MultiPaxSessionId,
    paxSessionId: string,
    lineOne: string,
    lineTwo: string | undefined,
    lineThree: string | undefined,
    city: string,
    state: string,
    postalCode: string,
    countryCode: string
  ): Promise<PaxSession> {
    const req: AddAddressRequest = {
      sessionId,
      paxSessionId,
      paxCheckAddAddressRequest: {
        address: {
          lineOne: lineOne,
          lineTwo: lineTwo,
          lineThree: lineThree,
          city: city,
          state: state,
          postalCode: postalCode,
          countryCode: countryCode,
        },
      },
      acceptLanguage: this.locale,
    };

    const res = await this.api.addAddress(req, addRandomRequestIdToHeader);

    if (res.data) {
      // success
      return PaxSessionFromApi(res.data.updatedPaxSession);
    }

    // general api error
    throw DataProviderGeneralException.fromApi(res.error);
  }

  private async recogniseDocumentFiles(
    sessionId: MultiPaxSessionId,
    paxSessionId: string,
    files: File[],
    method: PaxCheckDocumentProvisionMethod,
    expectedDocumentTypes: string[],
    pageIndexToProcess?: number
  ): Promise<PaxCheckRecogniseDocumentResult> {
    const recognitionReq: RecogniseDocumentRequest = {
      sessionId: sessionId,
      paxSessionId: paxSessionId,
      method: method,
      expectedDocumentTypes: expectedDocumentTypes,
      files: files,
      pageIndexToProcess: pageIndexToProcess,
    };

    const recognitionRes = await this.api.recogniseDocument(
      recognitionReq,
      addRandomRequestIdToHeader
    );

    if (recognitionRes.data) {
      // success (docs processing)
      return recognitionRes.data;
    }

    if (recognitionRes.error?.operationError) {
      // api error (docs processing)
      throw ApplyDocumentErrorWrapper.fromPaxCheckRecognizeDocumentError(
        recognitionRes.error.operationError
      );
    }

    // general api error (docs processing)
    throw DataProviderGeneralException.fromApi(recognitionRes.error);
  }

  async applyDocumentFiles(
    sessionId: MultiPaxSessionId,
    mainPaxSessionId: string,
    additionalPaxSessionIds: string[],
    files: File[],
    method: PaxCheckDocumentProvisionMethod,
    isTravelDocument: boolean,
    expectedDocumentTypes: string[],
    pageIndexToProcess?: number
  ): Promise<PaxSession[]> {
    // start with recognition
    const recognitionRes = await this.recogniseDocumentFiles(
      sessionId,
      mainPaxSessionId,
      files,
      method,
      expectedDocumentTypes,
      pageIndexToProcess
    );

    // apply recognised doc now
    const applyReq: ApplyRecognisedDocumentRequest = {
      sessionId: sessionId,
      paxSessionId: mainPaxSessionId,
      paxCheckApplyRecognisedDocumentRequest: {
        documents: recognitionRes.documents,
        documentType: recognitionRes.documentType,
        signature: recognitionRes.signature,
        method: method,
        isTravelDocument: isTravelDocument,
        additionalPaxIds: additionalPaxSessionIds,
      },
      acceptLanguage: this.locale,
    };

    const applyRes = await this.api.applyRecognisedDocument(
      applyReq,
      addRandomRequestIdToHeader
    );

    if (applyRes.data) {
      // success (apply)
      const additionalPaxSessions =
        applyRes.data.additionalPaxSessions
          ?.map((paxSession) => PaxSessionFromApi(paxSession))
          .filter(
            (pax) =>
              !this.chosenPaxSessionIds ||
              this.chosenPaxSessionIds.includes(pax.paxSessionId)
          ) || [];

      return [
        PaxSessionFromApi(applyRes.data.updatedPaxSession),
        ...additionalPaxSessions,
      ];
    }

    if (applyRes.error?.operationError) {
      // api error (apply)
      throw ApplyDocumentErrorWrapper.fromPaxCheckApplyDocumentError(
        applyRes.error.operationError
      );
    }

    // general api error (apply)
    throw DataProviderGeneralException.fromApi(applyRes.error);
  }

  async applyPassport(
    sessionId: MultiPaxSessionId,
    request: SubmitPassportRequest
  ): Promise<SubmitPassportResponse> {
    const req: ApplyTravelDocumentRequest = {
      sessionId,
      paxSessionId: request.paxSessionId,
      paxCheckApplyTravelDocumentRequest: {
        travelDocument: InternationalPassportToApiTravelDocument(
          request.passport
        ),
        ignoreMistakes: request.ignoreMistakes,
      },
      acceptLanguage: this.locale,
    };
    const res = await this.api.applyTravelDocument(
      req,
      addRandomRequestIdToHeader
    );

    if (res.data) {
      // success
      return ApplyPassportResponseFromApi(res.data);
    }

    if (res.error?.operationError) {
      // api error
      throw ApplyDocumentErrorWrapper.fromPaxCheckApplyDocumentError(
        res.error?.operationError
      );
    }

    // general api error
    throw DataProviderGeneralException.fromApi(res.error);
  }

  async applyManualDocument(
    sessionId: MultiPaxSessionId,
    paxSessionId: string,
    document: PaxCheckManualDocument
  ): Promise<SubmitManualDocumentResponse> {
    const req: ApplyManualDocumentRequest = {
      sessionId,
      paxSessionId,
      paxCheckApplyManualDocumentRequest: {
        document,
        ignoreMistakes: false,
      },
      acceptLanguage: this.locale,
    };

    const res = await this.api.applyManualDocument(
      req,
      addRandomRequestIdToHeader
    );

    if (res.data) {
      return ApplyManualDocumentResponseFromApi(res.data);
    }

    if (res.error?.operationError) {
      throw ApplyDocumentErrorWrapper.fromPaxCheckApplyDocumentError(
        res.error.operationError
      );
    }

    throw DataProviderGeneralException.fromApi(res.error);
  }

  async submitAnswers(
    sessionId: MultiPaxSessionId,
    paxSessionId: PaxSessionId,
    request?: SubmitAnswersRequest
  ): Promise<PaxSession> {
    const req: PostAnswersRequest = {
      sessionId,
      paxSessionId,
      paxCheckSetAnswersRequest: {
        nationality: request?.nationality,
        birthdate: request?.birthdate,
        answers: request?.answers,
        confirmDeclarations: request?.confirmDeclarations,
        refuteDeclarations: request?.refuteDeclarations,
      },
      acceptLanguage: this.locale,
    };

    const res = await this.api.postAnswers(req, addRandomRequestIdToHeader);

    if (res.data) {
      return PaxSessionFromApi(res.data);
    }

    // general api error
    throw DataProviderGeneralException.fromApi(res.error);
  }

  setLocale(locale: string) {
    this.locale = locale;
  }
}

export interface SubmitAnswersRequest {
  nationality?: string;
  birthdate?: Date;
  answers?: PaxCheckAnswer[];
  confirmDeclarations?: string[];
  refuteDeclarations?: string[];
}

export default DataProvider;
